import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  TablePagination,
  Chip,
} from '@mui/material';
import { Search as SearchIcon, People as PeopleIcon } from '@mui/icons-material';
import axios from 'axios';
import Layout from '../components/Layout';
import { formatDate } from '../utils/dateFormat';

const Guests = () => {
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    loadGuests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, search]);

  const loadGuests = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {
        page: page,
        page_size: pageSize,
      };
      if (search) {
        params.search = search;
      }
      const response = await axios.get('/crm/guests', { params });
      setGuests(response.data.guests || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      console.error('Error loading guests:', err);
      setError(err.response?.data?.detail || 'Failed to load guests');
      setGuests([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPage(0); // Reset to first page when searching
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangePageSize = (event) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Layout>
      <Container maxWidth={false} sx={{ width: '100%' }} sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" alignItems="center" gap={2}>
            <PeopleIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              Guests
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Paper sx={{ p: 2, mb: 2 }}>
          <TextField
            fullWidth
            placeholder="Search by name or email..."
            value={search}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Paper>

        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : (
          <>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Mobile</TableCell>
                    <TableCell>Date of Birth</TableCell>
                    <TableCell align="right">Bookings</TableCell>
                    <TableCell>Created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {guests.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          {search ? 'No guests found matching your search' : 'No guests found'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    guests.map((guest) => (
                      <TableRow key={guest.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {guest.first_name} {guest.last_name || ''}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {guest.email || (
                            <Typography variant="body2" color="text.secondary">
                              No email
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {guest.mobile_number || (
                            <Typography variant="body2" color="text.secondary">
                              No mobile
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {guest.date_of_birth ? (
                            formatDate(guest.date_of_birth, 'date')
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Not provided
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={guest.booking_count || 0}
                            size="small"
                            color={guest.booking_count > 0 ? 'primary' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          {guest.created_at ? (
                            <Typography variant="body2" color="text.secondary">
                              {formatDate(guest.created_at, 'date')}
                            </Typography>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={pageSize}
              onRowsPerPageChange={handleChangePageSize}
              rowsPerPageOptions={[25, 50, 100]}
            />
          </>
        )}
      </Container>
    </Layout>
  );
};

export default Guests;
